/**
 * PortfolioAIChat - AI-Powered Portfolio Assistant
 * TASK-206 Phase 3: AI Chat Assistant
 *
 * Natural language interface for querying portfolio data, analyzing scenarios,
 * and dynamically updating the UI based on conversational commands
 */

import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, Send, Sparkles, X, Minimize2, Maximize2, Loader2 } from 'lucide-react';
import AIQueryEngine, { QueryResult } from '../../services/AIQueryEngine';
import UIFilterController from '../../services/UIFilterController';
import { ClientLevelData } from '../../hooks/useClientLevelData';

export interface ChatMessage {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  suggestedQuestions?: string[];
}

// clientData accepts ClientLevelData, ClientDashboardData, or MyBookData.
// The AI engine casts internally to ClientLevelData for property access.
 
export interface PortfolioAIChatProps {
  clientData: ClientLevelData | any;
  onTabChange: (tab: string) => void;
  onFilterChange: (filter: any) => void;
  onHighlight: (section: string) => void;
  onScroll: (target: string) => void;
  className?: string;
}

const PortfolioAIChat: React.FC<PortfolioAIChatProps> = ({
  clientData,
  onTabChange,
  onFilterChange,
  onHighlight,
  onScroll,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Initialize AI engine and UI controller
  const aiEngine = useRef<AIQueryEngine | null>(null);
  const uiController = useRef<UIFilterController | null>(null);

  useEffect(() => {
    aiEngine.current = new AIQueryEngine(clientData);
    uiController.current = new UIFilterController({
      onTabChange,
      onFilterChange,
      onHighlight,
      onScroll
    });
  }, [clientData, onTabChange, onFilterChange, onHighlight, onScroll]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Welcome message when chat opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage: ChatMessage = {
        id: `msg-${Date.now()}`,
        type: 'assistant',
        content: `Hello! 👋 I'm your AI Portfolio Assistant.\n\nI can help you:\n• Filter positions across portfolios\n• Compare portfolio performance\n• Analyze USD/FX exposure\n• Find tax optimization opportunities\n• Show concentration risks\n• Simulate what-if scenarios\n\nTry asking me a question, or select from the suggestions below!`,
        timestamp: new Date(),
        suggestedQuestions: [
          "Show me all portfolios that have Apple",
          "What's my USD exposure?",
          "Find tax harvesting opportunities",
          "What are my top 3 risks?",
          "Compare Conservative and Aggressive portfolios"
        ]
      };
      setMessages([welcomeMessage]);
    }
  }, [isOpen, messages.length]);

  /**
   * Handle sending a message
   */
  const handleSendMessage = async (messageText?: string) => {
    const text = messageText || inputValue.trim();

    if (!text || !aiEngine.current || !uiController.current) return;

    // Add user message
    const userMessage: ChatMessage = {
      id: `msg-${Date.now()}`,
      type: 'user',
      content: text,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsProcessing(true);
    setShowSuggestions(false);

    try {
      // Simulate small delay for natural feel
      await new Promise(resolve => setTimeout(resolve, 500));

      // Process query with AI engine
      const result: QueryResult = await aiEngine.current.processQuery(text);

      // Execute UI commands if any
      if (result.uiCommands && result.uiCommands.length > 0) {
        await uiController.current.executeCommands(result.uiCommands);
      }

      // Add assistant response
      const assistantMessage: ChatMessage = {
        id: `msg-${Date.now()}-response`,
        type: 'assistant',
        content: result.response,
        timestamp: new Date(),
        suggestedQuestions: result.suggestedQuestions
      };

      setMessages(prev => [...prev, assistantMessage]);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error processing query:', error);

      const errorMessage: ChatMessage = {
        id: `msg-${Date.now()}-error`,
        type: 'assistant',
        content: "I'm sorry, I encountered an error processing your request. Please try again or rephrase your question.",
        timestamp: new Date(),
        suggestedQuestions: [
          "Show me all portfolios",
          "What are my top risks?",
          "Find optimization opportunities"
        ]
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Handle suggested question click
   */
  const handleSuggestionClick = (question: string) => {
    handleSendMessage(question);
  };

  /**
   * Handle Enter key press
   */
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  /**
   * Format timestamp
   */
  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('de-CH', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Floating chat button
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform z-50 ${className}`}
        title="Open AI Portfolio Assistant"
      >
        <Sparkles className="w-6 h-6 text-white" />
      </button>
    );
  }

  // Full chat interface
  return (
    <div
      className={`fixed bottom-6 right-6 ${
        isMinimized ? 'w-80' : 'w-96'
      } bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden z-50 flex flex-col ${
        isMinimized ? 'h-14' : 'h-[600px]'
      } transition-all ${className}`}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white">AI Portfolio Assistant</h3>
            <p className="text-xs text-white/80">Powered by Claude AI</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="text-white/80 hover:text-white transition-colors"
            title={isMinimized ? 'Maximize' : 'Minimize'}
          >
            {isMinimized ? (
              <Maximize2 className="w-4 h-4" />
            ) : (
              <Minimize2 className="w-4 h-4" />
            )}
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="text-white/80 hover:text-white transition-colors"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Messages Area */}
      {!isMinimized && (
        <>
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((message) => (
              <div key={message.id}>
                {/* Message Bubble */}
                <div
                  className={`flex ${
                    message.type === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg p-3 ${
                      message.type === 'user'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-gray-200 text-gray-900'
                    }`}
                  >
                    {message.type === 'assistant' && (
                      <div className="flex items-center gap-2 mb-2">
                        <MessageCircle className="w-4 h-4 text-blue-600" />
                        <span className="text-xs font-semibold text-gray-700">AI Assistant</span>
                      </div>
                    )}
                    <div className="text-sm whitespace-pre-line">{message.content}</div>
                    <div
                      className={`text-xs mt-2 ${
                        message.type === 'user' ? 'text-blue-100' : 'text-gray-500'
                      }`}
                    >
                      {formatTime(message.timestamp)}
                    </div>
                  </div>
                </div>

                {/* Suggested Questions */}
                {message.type === 'assistant' &&
                  message.suggestedQuestions &&
                  message.suggestedQuestions.length > 0 &&
                  showSuggestions &&
                  messages[messages.length - 1].id === message.id && (
                    <div className="mt-3 ml-2">
                      <p className="text-xs text-gray-500 mb-2">Suggested questions:</p>
                      <div className="flex flex-wrap gap-2">
                        {message.suggestedQuestions.map((question, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleSuggestionClick(question)}
                            className="text-xs px-3 py-1.5 bg-white border border-blue-300 text-blue-700 rounded-full hover:bg-blue-50 transition-colors"
                            disabled={isProcessing}
                          >
                            {question}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            ))}

            {/* Processing Indicator */}
            {isProcessing && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                  <span className="text-sm text-gray-600">Analyzing...</span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 border-t border-gray-200 bg-white">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about your portfolios..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                disabled={isProcessing}
              />
              <button
                onClick={() => handleSendMessage()}
                disabled={!inputValue.trim() || isProcessing}
                className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
                  inputValue.trim() && !isProcessing
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                title="Send message"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Press Enter to send • Shift+Enter for new line
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default PortfolioAIChat;
